import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { toast } from "sonner";
import { Modal } from "./Modal";
import { GhostButton } from "./Primitives";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { Calendar, MapPin, Users, Repeat } from "lucide-react";

/**
 * OpenShiftEditor — opens when a user clicks an Open/Understaffed row.
 * Scope is intentionally limited to guard assignment (per PRD Batch 4):
 * users can add/change guards on THIS shift OR on the whole recurring
 * series when a `recurrence_group` is present.
 */
export default function OpenShiftEditor({ row, onClose, onSaved }) {
  const [guards, setGuards] = useState([]);
  const [selected, setSelected] = useState(row.guard_ids || []);
  const [applyToSeries, setApplyToSeries] = useState(false);
  const [saving, setSaving] = useState(false);
  const hasSeries = !!row.recurrence_group;

  // Load only guards active on this shift's start-time so operators
  // aren't offered deactivated/on-leave staff.
  useEffect(() => {
    let cancelled = false;
    api.get("/guards").then((r) => {
      if (cancelled) return;
      const list = (r.data || []).filter((g) => g.status !== "inactive");
      setGuards(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const guardOptions = useMemo(
    () => guards.map((g) => ({
      value: g.id,
      label: g.name,
      sub: [g.badge_no && `#${g.badge_no}`, g.role].filter(Boolean).join(" · "),
    })).sort((a, b) => a.label.localeCompare(b.label)),
    [guards]
  );

  const save = async () => {
    if (selected.length === 0 && !window.confirm("Save this shift with NO guards assigned? It will show as 'open' again.")) return;
    setSaving(true);
    try {
      if (applyToSeries && hasSeries) {
        await api.put(`/shifts/series/${row.recurrence_group}`, { guard_ids: selected });
        toast.success("Series updated");
      } else {
        // Fetch full shift first so PUT /shifts/{id} has the required fields.
        const full = await api.get(`/shifts/${row.shift_id}`);
        const s = full.data;
        await api.put(`/shifts/${row.shift_id}`, {
          ...s,
          guard_ids: selected,
          guard_id: selected[0] || null,
        });
        toast.success("Shift updated");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Edit Open Shift" onClose={onClose} testId="open-shift-editor">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm border border-[#27272A] bg-[#0A0A0A] p-3">
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><MapPin className="size-3" /> Site</div>
            <div className="text-zinc-100 font-medium truncate">{row.site_name || "—"}</div>
            {row.client_name && <div className="text-xs text-zinc-500 truncate">{row.client_name}</div>}
          </div>
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><Calendar className="size-3" /> When</div>
            <div className="font-mono text-xs text-zinc-100">{new Date(row.start_time).toLocaleString()}</div>
            <div className="font-mono text-[11px] text-zinc-500">→ {new Date(row.end_time).toLocaleString()}</div>
          </div>
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><Users className="size-3" /> Currently</div>
            <div className="text-zinc-100">{row.guards_assigned} / {row.min_guards} required</div>
          </div>
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><Repeat className="size-3" /> Series</div>
            <div className="text-zinc-100">{hasSeries ? "Part of recurring series" : "One-off"}</div>
          </div>
        </div>

        <div>
          <div className="label-mute mb-1.5">Assigned Officers *</div>
          <MultiSelectDropdown
            testId="open-shift-editor-guards"
            label={selected.length ? `${selected.length} selected` : "Pick guards…"}
            options={guardOptions}
            selected={selected}
            onChange={setSelected}
            searchable
            triggerClassName="w-full"
            emptyHint="No active "
          />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
            Only  assignment is editable here. To change site, times, or min-officers,
            use the full Roster editor.
          </div>
        </div>

        {hasSeries && (
          <label className="flex items-start gap-2 border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 accent-[#FF9F0A]"
              checked={applyToSeries}
              onChange={(e) => setApplyToSeries(e.target.checked)}
              data-testid="open-shift-editor-apply-series"
            />
            <div className="text-xs">
              <div className="text-[#FF9F0A] font-bold uppercase tracking-widest mb-0.5">Apply to entire series</div>
              <div className="text-zinc-400">Update  assignment on <b>every future occurrence</b> of this shift, not just this one.</div>
            </div>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-[#27272A]">
          <GhostButton onClick={onClose} disabled={saving}>Cancel</GhostButton>
          <button
            type="button"
            data-testid="open-shift-editor-save"
            onClick={save}
            disabled={saving}
            className="border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white transition-colors px-4 py-1.5 text-xs uppercase tracking-widest disabled:opacity-50"
          >
            {saving ? "Saving…" : (applyToSeries ? "Save series" : "Save shift")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
